import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import './App.css';

// Category Card Component with dynamic height calculation
const CategoryCard = React.memo(({ item }) => {
  const containerRef = useRef(null);
  const [imageHeight] = useState(400); // Fixed height to prevent flicker

  // Memoize image URL with fixed height
  const imageUrl = useMemo(() => {
    return `https://vidaai.com/cdn-cgi/imagedelivery/Hr3zVE-rhnx6pqKYXYRDzw/${item.img}/h=${imageHeight}`;
  }, [item.img, imageHeight]);

  return (
    <Link 
      to={`/listing/${item.link}`}
      className="category-card-link"
    >
      <div className="category-card">
        <div className="category-image-container" ref={containerRef}>
          <img 
            src={imageUrl}
            alt={item.name} 
            className="category-card-image"
            loading="lazy"
          />
        </div>
        <div className="category-info-container">
          <h3 className="category-card-title">{item.name}</h3>
        </div>
      </div>
    </Link>
  );
});

function App() {
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  // Reusable header component
  const Header = ({ children }) => (
    <header className="App-header">
      {children}
    </header>
  );

  useEffect(() => {
    // Prevent duplicate fetch in strict mode
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.vidaai.com/api/v1/categories');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <div className="App">
        <Navigation />
        <Header>
          <div className="loading">Loading categories...</div>
        </Header>
        
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <Navigation />
        <Header>
          <div className="error">
            <p>Error loading categories: {error}</p>
            <p>Make sure the API server is running at https://api.vidaai.com</p>
          </div>
        </Header>
        
        <Footer />
      </div>
    );
  }

  const renderCategorySection = (title, categoryKey, items, description) => {
    if (!items || items.length === 0) return null;
    
    return (
      <div className="category-section">
        <h2 className="section-title">{title}</h2>
        <p className="section-description">{description}</p>
        <div className="categories-grid">
          {items.map((item, index) => (
            <CategoryCard 
              key={`${categoryKey}-${index}`}
              item={item}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <Navigation />
      <Header>
        <h1 className="main-page-title">Luxury Wedding Car Rentals</h1>
        <div className="categories-container">
          {renderCategorySection(
            "For the Couple", 
            "couple", 
            categories.couple,
            "Luxury decorated cars that make the bride and groom feel like royalty on their special day."
          )}
          {renderCategorySection(
            "For the Family", 
            "family", 
            categories.family,
            "Decorated SUVs and vans to comfortably transport close family members with style and elegance."
          )}
          {/* {renderCategorySection(
            "For the Baraat", 
            "baraat", 
            categories.baraat,
            "Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. Sed ut perspiciatis unde omnis iste natus error sit voluptatem."
          )} */}
        </div>

        <div className="cta-section">
          <h3>Make a Booking</h3>
          <p>Discuss your requirements with our expert over WhatsApp.</p>
          <a href="https://wa.me/message/JSEYCETSCIAIB1" target="_blank" rel="noopener noreferrer" className="cta-button">Chat on WhatsApp</a>
        </div>
      </Header>
      
      <Footer />
    </div>
  );
}

export default App;
